import useRequest from "@/lib/request/useRequest";
import { Button, Card, CardBody, Input, Spinner } from "@heroui/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useRef } from "react";
import useAuth from "@/lib/auth/useAuth";
import { getMapboxToken } from "@/lib/utils/map";
import { Organization } from "@/types/organization";
import useConfirm from "@/lib/confirm/useConfirm";
import sentinelHelmet from "@/assets/images/sentinel-helmet.png";
import sentryNetworkLogo from "@/assets/images/Sentry_Network_Logo.png";

export const Route = createFileRoute("/(auth)/register/select-csp")({
    component: RouteComponent,
});

type CspPreference = "preferred" | "default";

type NearbyCsp = {
    organization: {
        id: number;
        name: string;
        logo_url: string | null;
        csp_preference: CspPreference | null;
    };
    city: string | null;
    state: string | null;
    distance_miles: number | null;
};

function CspCard({
    csp,
    isElite,
    onSelect,
    isLoading,
}: {
    csp: NearbyCsp;
    isElite?: boolean;
    onSelect: () => void;
    isLoading: boolean;
}) {
    const locationText =
        [csp.city, csp.state].filter(Boolean).join(", ") || "—";

    const distanceText =
        typeof csp.distance_miles === "number"
            ? `${csp.distance_miles.toFixed(1)} mi`
            : null;

    return (
        <Card
            className={
                isElite
                    ? "border-2 border-brandNavy-500/30 bg-brandNavy-500/5"
                    : undefined
            }
        >
            <CardBody className="flex flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <div className="font-medium">
                                {csp.organization.name}
                            </div>
                            {isElite ? (
                                <span className="inline-flex items-center rounded-full bg-brandNavy-500/10 text-brandNavy-500 text-[11px] font-semibold px-2 py-0.5">
                                    ★ Elite
                                </span>
                            ) : null}
                        </div>
                        <div className="text-xs text-foreground-500">
                            {locationText}
                            {distanceText ? ` • ${distanceText}` : ""}
                        </div>
                    </div>
                </div>

                <div className="flex flex-row gap-2 items-center">
                    {csp.organization.logo_url ? (
                        <img
                            src={csp.organization.logo_url}
                            alt={`${csp.organization.name} logo`}
                            className="w-40 h-auto max-h-16 object-contain"
                        />
                    ) : null}

                    <Button
                        color="secondary"
                        onPress={onSelect}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                    >
                        Select
                    </Button>
                </div>
            </CardBody>
        </Card>
    );
}

function RouteComponent() {
    const navigate = useNavigate();
    const { useGetRequest, useMutateRequest } = useRequest();

    const confirm = useConfirm();

    const auth = useAuth();
    const didAutofill = useRef(false);

    const farmOrgId = (auth.user?.organization as Organization)?.id;

    const setServiceProviderMutation = useMutateRequest({
        method: "put",
        errorMessage: "Could not save your CSP selection. Please try again.",
    });

    const [address, setAddress] = useState("");

    const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(
        null,
    );

    const [isGeocoding, setIsGeocoding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [resolvedPlaceLabel, setResolvedPlaceLabel] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (address.trim()) return;

        if (didAutofill.current) return;

        const u = auth.user;
        if (!u) return;

        const composed = [u.address_1, u.address_2, u.city, u.state, u.zip]
            .filter(Boolean)
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(", ");

        if (!composed) return;

        setAddress(composed);
        didAutofill.current = true;
    }, [auth.user, address]);

    const nearbyCspQuery = useGetRequest<NearbyCsp[]>({
        url: "organizations/nearby_csps/",
        enabled: !!coords,
        params: coords ? { lat: coords.lat, lon: coords.lon } : undefined,
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const { eliteCsps, defaultCsps } = useMemo(() => {
        const rows = nearbyCspQuery.data ?? [];

        const dist = (c: NearbyCsp) =>
            typeof c.distance_miles === "number"
                ? c.distance_miles
                : Number.POSITIVE_INFINITY;

        const elite = rows
            .filter(
                (c) =>
                    c.organization.csp_preference === "preferred" &&
                    dist(c) <= 30,
            )
            .slice()
            .sort((a, b) => dist(a) - dist(b));

        const defaults = rows
            .filter(
                (c) =>
                    !(
                        c.organization.csp_preference === "preferred" &&
                        dist(c) <= 30
                    ),
            )
            .slice()
            .sort((a, b) => dist(a) - dist(b));

        return { eliteCsps: elite, defaultCsps: defaults };
    }, [nearbyCspQuery.data]);

    const hasResults = !!coords && eliteCsps.length + defaultCsps.length > 0;

    const helperText = useMemo(() => {
        if (!coords) return null;

        if (resolvedPlaceLabel) {
            return `Using location: ${resolvedPlaceLabel}`;
        }

        return `Using coordinates: ${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`;
    }, [coords, resolvedPlaceLabel]);

    function confirmSelectCsp(csp: NearbyCsp) {
        const locationText =
            [csp.city, csp.state].filter(Boolean).join(", ") || "—";

        const distanceText =
            typeof csp.distance_miles === "number"
                ? `${csp.distance_miles.toFixed(1)} mi`
                : null;

        confirm.showConfirm({
            title: "Select this CSP?",
            description: `${csp.organization.name} • ${locationText}${
                distanceText ? ` • ${distanceText}` : ""
            }`,
            confirmText: "Select",
            color: "primary",
            onConfirm: () => handleSelect(csp),
        });
    }

    async function handleSearch() {
        setError(null);

        const trimmed = address.trim();
        if (!trimmed) {
            setError("Enter an address to search.");
            return;
        }

        setIsGeocoding(true);
        setCoords(null);
        setResolvedPlaceLabel(null);

        try {
            const url = new URL(
                "https://api.mapbox.com/search/geocode/v6/forward",
            );

            url.searchParams.set("q", trimmed);
            url.searchParams.set("limit", "1");
            url.searchParams.set("autocomplete", "false");
            url.searchParams.set("types", "address,place,postcode");
            url.searchParams.set("access_token", getMapboxToken());

            const res = await fetch(url.toString(), { cache: "no-store" });
            if (!res.ok) {
                setError("Could not find that address. Please try again.");
                return;
            }
            const json = await res.json();
            const feature = json?.features?.[0];

            const lon = feature?.geometry?.coordinates?.[0];

            const lat = feature?.geometry?.coordinates?.[1];

            if (typeof lat !== "number" || typeof lon !== "number") {
                setError(
                    "Could not find a location for that address. Try a more specific address.",
                );
                return;
            }

            const label = String(
                feature?.properties?.full_address ?? "",
            ).trim();
            setResolvedPlaceLabel(label || null);

            setCoords({ lat, lon });
        } catch {
            setError("Could not find that address. Please try again.");
        } finally {
            setIsGeocoding(false);
        }
    }

    async function handleSelect(csp: NearbyCsp) {
        setError(null);

        if (!farmOrgId) {
            setError("Could not save your CSP selection. Please try again.");
            return;
        }

        try {
            await setServiceProviderMutation.mutateAsync({
                url: `organizations/${farmOrgId}/service_provider/`,
                data: { service_provider: csp.organization.id },
            });

            await auth.refreshUser?.();

            navigate({ to: "/" });
        } catch {
            setError("Could not save your CSP selection. Please try again.");
        }
    }

    const isNearbyLoading =
        !!coords && (nearbyCspQuery.isLoading || nearbyCspQuery.isFetching);
    const nearbyErrored = !!coords && nearbyCspQuery.isError;
    const showNoResults =
        !!coords && !isNearbyLoading && !nearbyErrored && !hasResults;

    return (
        <div className="flex flex-col items-center md:grid md:grid-cols-[1fr_2fr] min-h-screen w-full md:h-screen md:p-0">
            <div className="w-full h-full hidden md:block overflow-hidden">
                <div className="h-full w-full flex flex-col overflow-hidden text-primary-500">
                    <div className="p-4 flex flex-col gap-4 pt-12 w-full">
                        <div className="font-bold text-lg">
                            Sentinel® Insights
                        </div>
                        <div className="text-3xl font-semibold">
                            Find a Service Provider near you
                        </div>
                        <div>
                            Our Sentry Network of Certified Service Providers
                            (CSPs) turns Sentinel's nitrogen management
                            intelligence into profitable decisions. Our CSPs are
                            agronomy experts trained to leverage Sentinel
                            solutions alongside their local expertise to
                            maximize Sentinel's value. Find your CSP to get
                            started with Sentinel today.
                        </div>
                    </div>
                    <div className="w-full h-full flex justify-center items-center">
                        <img
                            src={sentinelHelmet}
                            className="w-[20rem] h-auto object-contain"
                        />
                    </div>
                </div>
            </div>
            <div className="pt-8 px-4 w-full md:overflow-y-auto md:h-screen flex flex-col items-center">
                <img src={sentryNetworkLogo} className="px-20 md:hidden" />
                <img src={sentryNetworkLogo} className="hidden md:block w-80" />
                <div className="flex flex-col gap-3 p-4 text-sm max-w-xl w-full mx-auto">
                    <div className="flex flex-col gap-1">
                        <div className="text-lg font-semibold">
                            Find a CSP near you
                        </div>
                        <div className="text-foreground-500">
                            Enter your address and we’ll show nearby service
                            providers.
                        </div>
                    </div>

                    <div className="flex gap-2 items-end">
                        <Input
                            label="Address"
                            value={address}
                            onValueChange={setAddress}
                            placeholder="Street Address, City, State, ZIP"
                        />
                        <Button
                            color="primary"
                            onPress={handleSearch}
                            isLoading={isGeocoding}
                        >
                            Search
                        </Button>
                    </div>

                    {helperText ? (
                        <div className="text-xs text-foreground-500">
                            {helperText}
                        </div>
                    ) : null}

                    {error ? (
                        <div className="text-danger text-sm">{error}</div>
                    ) : null}

                    {isNearbyLoading ? (
                        <div className="flex items-center gap-2 text-foreground-500">
                            <Spinner size="sm" />
                            Searching nearby CSPs…
                        </div>
                    ) : null}

                    {nearbyErrored ? (
                        <div className="text-danger text-sm">
                            Failed to load nearby CSPs. Please try again.
                        </div>
                    ) : null}

                    {showNoResults ? (
                        <div className="text-foreground-500">
                            No CSPs found within range.
                        </div>
                    ) : null}

                    {hasResults ? (
                        <div className="flex flex-col gap-4">
                            {eliteCsps.length ? (
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                                        Elite Providers
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        {eliteCsps.map((csp) => (
                                            <CspCard
                                                key={csp.organization.id}
                                                csp={csp}
                                                isElite
                                                onSelect={() =>
                                                    confirmSelectCsp(csp)
                                                }
                                                isLoading={
                                                    setServiceProviderMutation.isPending
                                                }
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {defaultCsps.length ? (
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                                        Providers
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        {defaultCsps.map((csp) => (
                                            <CspCard
                                                key={csp.organization.id}
                                                csp={csp}
                                                onSelect={() =>
                                                    confirmSelectCsp(csp)
                                                }
                                                isLoading={
                                                    setServiceProviderMutation.isPending
                                                }
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="md:hidden mt-12 text-primary-500 flex-1 flex flex-col">
                <div className="p-4 flex flex-col gap-4 pt-12 w-full">
                    <div className="font-bold text-lg">Sentinel® Insights</div>
                    <div className="text-3xl font-semibold">
                        Find a Service Provider near you
                    </div>
                    <div>
                        Our Sentry Network of Certified Service Providers (CSPs)
                        turns Sentinel's nitrogen management intelligence into
                        profitable decisions. Our CSPs are agronomy experts
                        trained to leverage Sentinel solutions alongside their
                        local expertise to maximize Sentinel's value. Find your
                        CSP to get started with Sentinel today.
                    </div>
                </div>
                <div className="w-full flex justify-center pb-12">
                    <img
                        src={sentinelHelmet}
                        className="w-[40%] h-auto object-contain"
                    />
                </div>
            </div>
        </div>
    );
}
